import type { Country, DeliveryZoneCode } from '@noths/polaris-client-localisation';
import {
  countries,
  deliveryZoneCodeValues,
  ukDeliveryZones,
} from '@noths/polaris-client-localisation';
import type { DropDownOption } from '@noths/polaris-client-ribbons-design-system';
import { createSelector } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { GroupedCountries } from 'src/redux/delivery/types';
import { getMinMaxDeliveryEstimate } from './utils/getMinMaxDeliveryEstimate';

const UK_DROPDOWN_OPTIONS_LABEL = 'United Kingdom';
const OTHER_COUNTRIES_DROPDOWN_OPTIONS_LABEL = 'Other countries';

export const selectSelectedZoneCode = ({ delivery }: ReduxApplicationState) =>
  delivery.selectedZone;

export const selectSelectedCountry = ({ delivery }: ReduxApplicationState) =>
  delivery.selectedCountry;

export const selectDeliveryZones = ({ delivery }: ReduxApplicationState) => delivery.zones;

export const selectDeliveryOptionsForZone = createSelector(
  [selectSelectedZoneCode, selectDeliveryZones],
  (selectedZone, zones) =>
    zones.find(({ zoneCode }) => zoneCode === selectedZone)?.deliveryOptions || [],
);

export const selectMinMaxDeliveryEstimates = createSelector(
  [selectDeliveryOptionsForZone],
  (countryDeliveryOptions) => getMinMaxDeliveryEstimate(countryDeliveryOptions),
);

export const selectZonesDeliveredTo = createSelector(
  [selectDeliveryZones],
  (options): DeliveryZoneCode[] => options.map(({ zoneCode }) => zoneCode),
);

export const selectHasUKDeliveryOnly = createSelector(
  [selectZonesDeliveredTo],
  (zonesDeliveredTo) => !zonesDeliveredTo.some((zoneCode) => !ukDeliveryZones.includes(zoneCode)),
);

export const selectHasSelectedCountriesDeliveryOnly = createSelector(
  [selectHasUKDeliveryOnly, selectZonesDeliveredTo],
  (hasUKDeliveryOnly, zonesDeliveredTo): boolean =>
    !(
      hasUKDeliveryOnly ||
      deliveryZoneCodeValues.every((zoneCode) =>
        zonesDeliveredTo.includes(zoneCode as DeliveryZoneCode),
      )
    ),
);

export const selectCountriesDeliveredTo = createSelector(
  [selectZonesDeliveredTo],
  (zonesDeliveredTo): Country[] =>
    countries.filter(({ deliveryZoneCode }) =>
      zonesDeliveredTo.includes(deliveryZoneCode as DeliveryZoneCode),
    ),
);

const createDropDownOptionFromCountry = ({
  countryCode,
  deliveryZoneCode,
  name,
}: Country): DropDownOption => ({
  optionLabel: name,
  optionValue: `${countryCode}|${deliveryZoneCode}${name === 'United Kingdom' ? `|UK` : ''}`,
});

const isUsersCountry =
  (userCountryCode?: Country['countryCode']) =>
  ({ countryCode }: Country) =>
    countryCode === userCountryCode;

const selectUserCountryCode = (state: ReduxApplicationState) => {
  return state.user?.countryCode;
};

const selectCountriesForDropdown = createSelector(
  [selectCountriesDeliveredTo, selectUserCountryCode],
  (countriesDeliveredTo, userCountryCode): DropDownOption[] => {
    const countryDropDownOptions = countriesDeliveredTo.map(createDropDownOptionFromCountry);

    if (countriesDeliveredTo.some(isUsersCountry(userCountryCode))) {
      return countryDropDownOptions;
    }

    const selectedCountryData = countries.find(isUsersCountry(userCountryCode));

    return (
      selectedCountryData ? [createDropDownOptionFromCountry(selectedCountryData)] : []
    ).concat(countryDropDownOptions);
  },
);

export const selectGroupedCountriesForDropdown = createSelector(
  selectCountriesForDropdown,
  (countriesOptions): GroupedCountries => {
    const groups = countriesOptions.reduce<Record<string, DropDownOption[]>>((acc, country) => {
      const [countryCode] = country.optionValue.split('|');
      const isUKCountryCode = countryCode === 'GB' || countryCode === 'UK';
      const key = isUKCountryCode
        ? UK_DROPDOWN_OPTIONS_LABEL
        : OTHER_COUNTRIES_DROPDOWN_OPTIONS_LABEL;

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(country);

      return acc;
    }, {});

    return groups as GroupedCountries;
  },
);
